import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAccount, useDisconnect, useWalletClient } from 'wagmi';
import api from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';
import UsernameSetupModal from '../components/UsernameSetupModal';
import { cacheSignature, getCachedSignature, hasValidCachedSignatures } from '../utils/signatureCache';
import { appKit, wagmiAdapter } from '../config/appkit';
import { passportInstance, logoutFromPassport } from '../config/passport';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();
  const { data: walletClient } = useWalletClient();
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [showUsernameModal, setShowUsernameModal] = useState(false);

  // Store wallet address for use in signature cache
  useEffect(() => {
    if (address) {
      localStorage.setItem('wallet_address', address);
    } else {
      localStorage.removeItem('wallet_address');
    }
  }, [address]);

  // Sign message with caching
  const signMessage = useCallback(async (messageToSign) => {
    if (!walletClient || !address) {
      throw new Error('Wallet client not available');
    }

    // Check if we have a cached signature for this message
    const cachedSignature = getCachedSignature(messageToSign, address);
    if (cachedSignature) {
      console.log('Using cached signature');
      return cachedSignature;
    }

    // Check if we have a cached auth token that's still valid
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const parsedUser = savedUser ? JSON.parse(savedUser) : null;
    const isTimestampValid = parsedUser?.timestamp && (Date.now() - parsedUser.timestamp < SESSION_EXPIRY);

    if (token && isTimestampValid && parsedUser?.address === address) {
      console.log('Valid auth token exists, skipping signature request');
      // Create a fake signature that we can cache
      const fakeSignature = `cached_auth_token_${Date.now()}`;
      cacheSignature(messageToSign, fakeSignature, address);
      return fakeSignature;
    }

    // No cached signature or valid token, request a new one
    console.log('Requesting new signature...');
    const signature = await walletClient.signMessage({
      message: messageToSign
    });

    // Cache the signature for future use
    cacheSignature(messageToSign, signature, address);
    return signature;
  }, [walletClient, address]);

  // Session expiry time (14 days in milliseconds)
  const SESSION_EXPIRY = 14 * 24 * 60 * 60 * 1000;

  useEffect(() => {
    const connectWallet = async () => {
      try {
        // First check if we have a valid stored session
        const savedUser = localStorage.getItem('user');
        const parsedUser = savedUser ? JSON.parse(savedUser) : null;
        const token = localStorage.getItem('token');
        const isTimestampValid = parsedUser?.timestamp && (Date.now() - parsedUser.timestamp < SESSION_EXPIRY);
        const hasCachedSignatures = address ? hasValidCachedSignatures(address) : false;
        const storedAddress = parsedUser?.address || localStorage.getItem('wallet_address');
        const walletProvider = localStorage.getItem('wallet_provider');
        const passportAddress = localStorage.getItem('passport_address');
        const passportUserInfo = localStorage.getItem('passport_user_info');
        const isPassport = walletProvider === 'passport' || (passportAddress && passportUserInfo) || parsedUser?.provider === 'passport';

        console.log(`[AuthContext] Session Check: User Address=${storedAddress}, Current Address=${address}, Address Match=${storedAddress === address}, Token Found=${!!token}, Timestamp Valid=${isTimestampValid}, Has Cached Signatures=${hasCachedSignatures}, Is Connected=${isConnected}, Provider=${walletProvider}, Is Passport=${isPassport}`);

        // Skip AppKit authentication if using Passport
        if (isPassport) {
          console.log('AuthContext: Using Passport authentication, skipping AppKit flow');

          // If we have a valid Passport session but no connection, try to restore it
          if (storedAddress && token && isTimestampValid && !isConnected) {
            console.log('AuthContext: Valid Passport session found but not connected. Attempting to reconnect...');

            try {
              // Try to reconnect using Passport
              await passportInstance.connectEvm();
              console.log('AuthContext: Passport connection restored');

              // If we have a valid user object, reuse it
              if (parsedUser) {
                console.log('AuthContext: Reusing existing Passport user session');
                setUser(parsedUser);
              }
            } catch (error) {
              console.error('AuthContext: Failed to restore Passport connection:', error);
            }
          }

          // Exit early - don't proceed with AppKit authentication
          return;
        }

        // If we have a valid session but no wallet connection, try to restore it
        if (storedAddress && token && (isTimestampValid || hasCachedSignatures) && !isConnected && !isPassport) {
          console.log('AuthContext: Valid AppKit session found but wallet not connected. Attempting to reconnect wallet...');

          // Default to AppKit/Wagmi
          try {
            // Try to reconnect using wagmiAdapter first
            await wagmiAdapter.connect();
            console.log('AuthContext: Wallet connection restored via wagmiAdapter');

            // Mark as AppKit provider
            localStorage.setItem('wallet_provider', 'appkit');
          } catch (error) {
            console.log('AuthContext: Failed to restore via wagmiAdapter, trying appKit...', error);
            try {
              await appKit.connect();
              console.log('AuthContext: Wallet connection restored via appKit');

              // Mark as AppKit provider
              localStorage.setItem('wallet_provider', 'appkit');
            } catch (innerError) {
              console.error('AuthContext: Failed to restore wallet connection:', innerError);
            }
          }

          // Wait a moment for connection to establish
          await new Promise(resolve => setTimeout(resolve, 1000));
        }

        // Now check if wallet is connected via AppKit
        if (isConnected && address && !isPassport) {
          // Mark as AppKit provider
          localStorage.setItem('wallet_provider', 'appkit');

          // Check if we already have a valid signature for this address
          const isAddressMatch = parsedUser?.address === address;

          // Check if we have a valid user object AND token in localStorage
          // Ensure parsedUser exists before checking timestamp
          if (isAddressMatch && token && (isTimestampValid || hasCachedSignatures)) {
            console.log('AuthContext: Found valid AppKit user session in localStorage. Reusing.');

            // Always extend the session timestamp when reusing
            const now = Date.now();
            const updatedUser = {
              ...parsedUser,
              timestamp: now,
              sessionExpiry: now + SESSION_EXPIRY,
              isConnected: true, // Ensure isConnected is set to true
              provider: 'appkit' // Explicitly mark as AppKit
            };
            localStorage.setItem('user', JSON.stringify(updatedUser));

            // Also update the token's timestamp if it's in the legacy format
            try {
              if (!token.includes('.')) { // Not a JWT token
                const decodedToken = JSON.parse(atob(token));
                decodedToken.timestamp = now;
                decodedToken.expiry = now + SESSION_EXPIRY;
                decodedToken.provider = 'appkit'; // Explicitly mark as AppKit
                const updatedToken = btoa(JSON.stringify(decodedToken));
                localStorage.setItem('token', updatedToken);
              }
            } catch (error) {
              console.warn('Failed to update token timestamp:', error);
            }

            setUser(updatedUser);
            return; // Exit connectWallet function early
          }

          // --- No valid session found, proceed with signing ---
          console.log('AuthContext: No valid AppKit session found or session expired. Requesting signature.');

          // Construct the dynamic message the backend expects
          const timestamp = Date.now();
          const messageToSign = `Authenticate with BitPool for address ${address} at timestamp ${timestamp}`; // Dynamic message

          // Check if we have a cached signature for this message
          const cachedSignature = getCachedSignature(messageToSign, address);
          let signature;

          if (cachedSignature) {
            console.log('AuthContext: Using cached signature');
            signature = cachedSignature;
          } else {
            // No cached signature, request a new one
            console.log('AuthContext: No cached signature found, requesting new signature');
            signature = await signMessage(messageToSign);
            console.log('AuthContext: Signed message:', messageToSign);
            console.log('AuthContext: Signature:', signature);
          }

          // Generate base64 token string (without "Bearer ")
          const base64Token = btoa(JSON.stringify({
            address,
            timestamp,
            message: messageToSign, // Embed the dynamic message
            signature, // Signature of the dynamic message
            expiry: timestamp + SESSION_EXPIRY, // Add explicit expiry timestamp
            provider: 'appkit' // Explicitly mark as AppKit
          }));

          // Store only the base64 string in localStorage
          localStorage.setItem('token', base64Token);

          // Create basic user, including the base64 token itself if needed elsewhere immediately
          const basicUser = {
            address,
            isConnected,
            provider: 'appkit', // Explicitly mark as AppKit
            token: base64Token,
            timestamp,
            sessionExpiry: timestamp + SESSION_EXPIRY
          };

          // Store wallet connection info for reconnection
          localStorage.setItem('wallet_connected', 'true');
          localStorage.setItem('wallet_address', address);
          localStorage.setItem('wallet_last_connected', timestamp.toString());
          localStorage.setItem('wallet_provider', 'appkit'); // Explicitly mark as AppKit

          // Set basic user info
          localStorage.setItem('user', JSON.stringify(basicUser));
          setUser(basicUser);

          // Get user info after token is set
          try {
            const userInfo = await api.get(API_ENDPOINTS.wallet.get(address));
            const fullUser = {
              ...basicUser,
              isAdmin: userInfo.isAdmin || false,
              username: userInfo.username,
              bitpBalance: userInfo.bitpBalance,
              usdBalance: userInfo.usdBalance,
              token: base64Token // Ensure token is part of the full user object
            };

            localStorage.setItem('user', JSON.stringify(fullUser));
            setUser(fullUser);

            // Check if username is set and show modal if not
            if (!userInfo.username || userInfo.username.trim() === '') {
              console.log('Username not set, showing setup modal');
              setShowUsernameModal(true);
            } else {
              console.log('Username already set:', userInfo.username);
              // Mark as shown to prevent it from appearing elsewhere
              localStorage.setItem(`username_modal_shown_${fullUser.address}`, 'true');
            }
          } catch (error) {
            console.error('Error getting user info:', error);
            // Keep using basic user info if getting additional info fails
          }
        } else if (!isConnected && !isPassport) {
          // No wallet connection and not using Passport
          console.log('AuthContext: No wallet connection available and not using Passport');

          // Only clear AppKit-related data if we're not using Passport
          if (walletProvider !== 'passport') {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Error in wallet connection process:', error);

        // Only clear data if we're not using Passport
        if (localStorage.getItem('wallet_provider') !== 'passport') {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
    };

    connectWallet();
  }, [isConnected, address, walletClient, signMessage]); // Dependencies

  // Real-time wallet updates are now handled by polling in the Wallet component

  // Effect to check if username is set whenever user state changes
  useEffect(() => {
    if (user && user.address) {
      // Check if the user has a username
      if (!user.username || user.username.trim() === '') {
        // Check if we've already shown the modal for this user
        const hasShownModal = localStorage.getItem(`username_modal_shown_${user.address}`);

        if (!hasShownModal) {
          console.log('Username not set, showing setup modal');
          setShowUsernameModal(true);
        }
      } else {
        console.log('User has username:', user.username);
        // Mark as shown since the user already has a username
        localStorage.setItem(`username_modal_shown_${user.address}`, 'true');
      }
    }
  }, [user]);

  // No WebSocket wallet balance update listener needed anymore

  const logout = async () => {
    // Check if we're already in a logout process to prevent loops
    const logoutInProgress = sessionStorage.getItem('logout_in_progress');
    if (logoutInProgress === 'true') {
      console.log('Logout already in progress, preventing loop');
      return;
    }

    // Set a flag to prevent logout loops
    sessionStorage.setItem('logout_in_progress', 'true');

    try {
      const walletProvider = localStorage.getItem('wallet_provider');

      // Determine if user is using Passport by checking multiple sources
      const passportAddress = localStorage.getItem('passport_address');
      const passportUserInfo = localStorage.getItem('passport_user_info');
      const isUsingPassport = walletProvider === 'passport' ||
                             (passportAddress && passportUserInfo) ||
                             user?.provider === 'passport' ||
                             user?.passportAddress;

      console.log('Logging out user with provider:', walletProvider);
      console.log('Is using Passport:', isUsingPassport);

      // Handle provider-specific logout first
      if (isUsingPassport) {
        console.log('Performing Passport-specific logout');
        try {
          // Passport-specific cleanup - do this first to ensure state is cleared
          localStorage.removeItem('passport_user_info');
          localStorage.removeItem('passport_address');
          localStorage.removeItem('wallet_provider');

          // Clear user state before calling logout to prevent loops
          setUser(null);

          // Perform the actual logout from Passport
          await logoutFromPassport();
        } catch (error) {
          console.error('Error logging out from Passport:', error);
        }
      } else {
        console.log('Performing AppKit-specific logout');
        // Default to AppKit/Wagmi disconnect
        disconnect();
      }
    } finally {
      // Clear the logout flag after a short delay
      setTimeout(() => {
        sessionStorage.removeItem('logout_in_progress');
      }, 1000);
    }

    // Clear common local storage data
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('wallet_connected');
    localStorage.removeItem('wallet_address');
    localStorage.removeItem('wallet_provider');
    localStorage.removeItem('wallet_last_connected');

    // Clear any reconnect flags
    localStorage.removeItem('wallet_reconnect_requested');

    // Update state
    setUser(null);

    console.log('Logout completed successfully');
  };

  const refreshWalletBalance = async () => {
    if (user?.address) {
      try {
        console.log('AuthContext: Refreshing wallet balance for address:', user.address);

        // Fetch wallet data directly from API
        const response = await api.get(API_ENDPOINTS.wallet.get(user.address));
        const data = response;

        console.log('AuthContext: Wallet data received:', data);

        // Make sure we have numeric values for the balances
        const formatBalance = (value) => {
          const num = parseFloat(value || 0);
          return isNaN(num) ? '0.00' : num.toString();
        };

        // Format the balances for both standard naming and uppercase
        // Safely extract currencies - handle both direct fields and the wallet model format
        const baiBalance = data.bai !== undefined ? data.bai : (data.baiBalance || 0);
        const bnbBalance = data.bnb !== undefined ? data.bnb : (data.bnbBalance || 0);
        const imxBalance = data.imx !== undefined ? data.imx : (data.imxBalance || 0);
        const usdtBalance = data.usdt !== undefined ? data.usdt : (data.usdtBalance || 0);

        // Prepare standardized wallet balances object
        const walletBalances = {
          BAI: formatBalance(baiBalance),
          BNB: formatBalance(bnbBalance),
          IMX: formatBalance(imxBalance),
          USDT: formatBalance(usdtBalance)
        };

        // Update user with new balances for all currencies
        const updatedUser = {
          ...user,
          baiBalance: formatBalance(baiBalance),
          bnbBalance: formatBalance(bnbBalance),
          imxBalance: formatBalance(imxBalance),
          usdtBalance: formatBalance(usdtBalance),
          // Add the wallet balances object for components that expect it
          walletBalances
        };

        console.log('AuthContext: Formatted wallet balances:', walletBalances);
        // Debug log to verify balances are correct
        console.log('AuthContext: Raw API response:', data);
        console.log('AuthContext: Extracted balance fields:', {
          baiBalance, bnbBalance, imxBalance, usdtBalance
        });

        // Update localStorage and state atomically to ensure consistency
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);

        console.log('AuthContext: Wallet balances refreshed successfully');
        return updatedUser;
      } catch (error) {
        console.error('AuthContext: Error refreshing wallet balance:', error);
        return user;
      }
    }
    return user;
  };

  // Function to handle profile update
  const updateUserProfile = (profileData) => {
    if (user && profileData) {
      console.log('AuthContext: Updating user profile with:', profileData);

      // Create updated user object with new profile data
      const updatedUser = {
        ...user,
        username: profileData.username,
        email: profileData.email || user.email
      };

      // Update localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Update state
      setUser(updatedUser);

      console.log('AuthContext: User profile updated successfully');
      return updatedUser;
    }
    return user;
  };

  // Function to update parts of the user context
  const updateUserContext = (updates) => {
    setUser(currentUser => {
      if (!currentUser && !updates) return null;

      // If we have updates but no current user, use updates as the new user
      const updatedUser = currentUser ? { ...currentUser, ...updates } : { ...updates };

      // Store in localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Dispatch a custom event to notify other components about the authentication update
      const authEvent = new CustomEvent('auth-context-updated', {
        detail: {
          user: updatedUser
        }
      });
      window.dispatchEvent(authEvent);

      console.log('Auth context updated with:', updatedUser);
      return updatedUser;
    });
  };


  // Add a function to check if the user is authenticated with Passport
  const isPassportUser = useCallback(() => {
    // Check multiple indicators to determine if the user is authenticated with Passport
    const walletProvider = localStorage.getItem('wallet_provider');
    const passportAddress = localStorage.getItem('passport_address');
    const passportUserInfo = localStorage.getItem('passport_user_info');

    // Log the values for debugging
    console.log('isPassportUser check:', {
      userProvider: user?.provider,
      userPassportAddress: user?.passportAddress,
      userPassportInfo: !!user?.passportInfo,
      walletProvider,
      passportAddress: !!passportAddress,
      passportUserInfo: !!passportUserInfo
    });

    return (
      user?.provider === 'passport' ||
      user?.passportAddress ||
      user?.passportInfo ||
      walletProvider === 'passport' ||
      (passportAddress && passportUserInfo)
    );
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user,
      logout,
      isConnected,
      refreshWalletBalance,
      setShowUsernameModal,
      updateUserContext, // Expose the update function
      isPassportUser // Add the helper function
    }}>
      {children}
      <UsernameSetupModal
        isOpen={showUsernameModal}
        onClose={() => {
          setShowUsernameModal(false);
          // Mark that we've shown the modal for this user
          if (user?.address) {
            localStorage.setItem(`username_modal_shown_${user.address}`, 'true');
          }
        }}
        userAddress={user?.address || ''}
        onProfileSet={updateUserProfile}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
